import React, { useEffect, useRef } from 'react';

const advantages = [
    {
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                <circle cx="12" cy="9" r="2.5"/>
            </svg>
        ),
        title: 'Local experts',
        desc: 'Our guides are born and raised in Central Asia — they know every trail, village, and hidden gem firsthand.',
    },
    {
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="7" r="3"/>
                <circle cx="16" cy="7" r="3"/>
                <path d="M3 21v-2a5 5 0 0 1 5-5h2"/>
                <path d="M13 21v-2a5 5 0 0 1 5-5h1a5 5 0 0 1 5 5v2"/>
            </svg>
        ),
        title: 'Small groups / private tours',
        desc: 'We keep groups intimate — up to 12 people — so you get personal attention and a richer experience.',
    },
    {
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402C1 3.124 5.068 1 7.5 1c1.887 0 3.736.957 4.5 2.19C12.764 1.957 14.613 1 16.5 1 18.932 1 23 3.124 23 7.191c0 4.105-5.37 8.863-11 14.402z"/>
            </svg>
        ),
        title: 'Authentic experiences',
        desc: 'Stay with nomadic families, eat home-cooked meals, and join local festivals — no tourist traps.',
    },
    {
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.24h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l1.03-.94a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
        ),
        title: '24/7 support',
        desc: 'From booking to the last day of your trip, our team is reachable around the clock for any question or emergency.',
    },
    {
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12h18M3 6h18M3 18h12"/>
                <path d="M17 15l3 3-3 3"/>
            </svg>
        ),
        title: 'Flexible itineraries',
        desc: 'Every route is tailored to your pace, interests, and budget — we build your trip around you, not a fixed schedule.',
    },
];

/* Чётные (0,2,4) — слева, нечётные (1,3) — справа */
const getRevealClass = (i) => {
    if (i % 2 === 0) return 'reveal-left';
    return 'reveal-right';
};

const Adv = () => {
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => {
                if (e.isIntersecting) e.target.classList.add('visible');
            }),
            { threshold: 0.15 }
        );
        const els = ref.current?.querySelectorAll('.reveal-left, .reveal-right, .reveal');
        els?.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className="bg-dark py-24 md:py-36 overflow-hidden">
            <div className="container">
                {/* Header */}
                <div className="reveal flex flex-col items-center gap-4 mb-16 text-center">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-px bg-it-green opacity-90" />
                        <p className="font-unbounded text-white text-xs uppercase tracking-widest">Why choose us</p>
                        <div className="w-8 h-px bg-gold opacity-70" />
                    </div>
                    <h2 className="font-unbounded font-bold text-4xl md:text-5xl text-white">Our advantages</h2>
                </div>

                {/* Horizontal scrollable row */}
                <div className="overflow-x-auto pb-4 -mx-4 px-4 md:overflow-x-visible md:mx-0 md:px-0">
                    <div className="flex gap-5 md:grid md:grid-cols-5 min-w-max md:min-w-0">
                        {advantages.map((adv, i) => (
                            <div
                                key={i}
                                className={`${getRevealClass(i)} card-dark p-7 flex flex-col items-center text-center gap-5 group cursor-default w-56 md:w-auto flex-shrink-0 md:flex-shrink`}
                                style={{ transitionDelay: `${i * 0.12}s` }}
                            >
                                {/* Icon */}
                                <div className="w-16 h-16 rounded-2xl bg-green-dim border border-green-border flex items-center justify-center text-gold transition-all duration-300 group-hover:border-gold/40 group-hover:bg-dark-3 group-hover:glow-gold">
                                    <span className="transition-transform duration-300 group-hover:scale-110">
                                        {adv.icon}
                                    </span>
                                </div>

                                {/* Title */}
                                <h5 className="font-unbounded font-bold text-sm text-white group-hover:text-gold transition-colors duration-300 leading-snug">
                                    {adv.title}
                                </h5>

                                {/* Description */}
                                <p className="font-manrope text-white/55 text-xs leading-relaxed">{adv.desc}</p>

                                {/* Bottom accent line */}
                                <div className="w-0 h-0.5 bg-gold rounded-full transition-all duration-500 group-hover:w-12 mt-auto" />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Scroll hint on mobile */}
                <p className="md:hidden text-center font-manrope text-white/20 text-xs mt-4 tracking-widest uppercase">
                    scroll →
                </p>
            </div>
        </div>
    );
};

export default Adv;
